import React, { useEffect } from 'react';
import { Bolt, Code, Smartphone, FileEdit, RefreshCcw, Database } from 'lucide-react';
import { cn } from '@/lib/utils';
interface BenefitProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  delay?: number;
}
const Benefit = ({
  icon,
  title,
  description,
  delay = 0
}: BenefitProps) => {
  return <div className="benefit-card opacity-0 translate-y-8 transition-all duration-700 delay-100 bg-white rounded-xl p-6 shadow-md hover:shadow-lg hover:-translate-y-1 transition-all duration-300 flex flex-col items-center text-center">
      <div className="mb-4 text-primary p-3 bg-primary/10 rounded-full">{icon}</div>
      <h3 className="text-xl font-semibold mb-2 text-secondary">{title}</h3>
      <p className="text-gray-600">{description}</p>
    </div>;
};
const BenefitsSection = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Cast the target to HTMLElement which has the dataset property
          const htmlElement = entry.target as HTMLElement;
          setTimeout(() => {
            entry.target.classList.add('opacity-100', 'translate-y-0');
          }, parseInt(htmlElement.dataset.delay || '0', 10));
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    });
    document.querySelectorAll('.benefit-card').forEach((el, index) => {
      // Cast to HTMLElement before using dataset
      (el as HTMLElement).dataset.delay = (index * 100).toString();
      observer.observe(el);
    });
    return () => {
      document.querySelectorAll('.benefit-card').forEach(el => {
        observer.unobserve(el);
      });
    };
  }, []);
  const benefits = [{
    icon: <Bolt className="h-8 w-8" />,
    title: "Lightning-Fast Delivery",
    description: "Get your email templates ready to launch in record time"
  }, {
    icon: <Code className="h-8 w-8" />,
    title: "Clean, Responsive Code",
    description: "Expertly crafted HTML & CSS that passes all quality checks"
  }, {
    icon: <Smartphone className="h-8 w-8" />,
    title: "Device Optimization",
    description: "Perfect display across all devices and email clients"
  }, {
    icon: <FileEdit className="h-8 w-8" />,
    title: "Tailored Content",
    description: "Professional copy and content creation for higher engagement"
  }, {
    icon: <RefreshCcw className="h-8 w-8" />,
    title: "Revisions Included",
    description: "Fine-tune your templates until they're exactly right"
  }, {
    icon: <Database className="h-8 w-8" />,
    title: "CRM Integration",
    description: "Seamless connection with your existing CRM platform"
  }];
  return <section id="benefits" className="px-4 bg-gradient-to-b from-blue-50 to-white py-[40px]">
      <div className="container mx-auto">
        <div className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Why Choose <span className="text-primary">AD Email Lab</span>?</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto px-0">We combine technical expertise with creative flair to deliver CRM emails that drive results.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {benefits.map((benefit, index) => <Benefit key={index} icon={benefit.icon} title={benefit.title} description={benefit.description} delay={index * 100} />)}
        </div>
      </div>
    </section>;
};
export default BenefitsSection;